import * as React from 'react';

import { cn } from '@/lib/shadcn/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { type VariantProps } from 'class-variance-authority';
import { buttonVariants } from './button-variants';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@/components/typography/typography';

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  children?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      contentType,
      isLoading,
      children,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className, contentType }))}
        ref={ref}
        {...props}
        disabled={isLoading || props.disabled}
        aria-disabled={isLoading || props.disabled}
      >
        {isLoading ? (
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <Typography.p1>{t('buttons.loading')}</Typography.p1>
          </div>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button };
